import { Controller, useFormContext } from "react-hook-form";
import { Flex } from "../../../../../components/Flex";
import { Text } from "../../../../../components/Text";
import { useTranslation } from "react-i18next";
import { TestIDs } from "../../../../../tests/types/testIds";
import { Lengths } from "../../../../../model/shared/enum/Lengths.enum";
import { Input } from "../../../../../components/Input";
import { Info } from "../../../../../components/Info";
import styled from "styled-components";
import { Spacing } from "../../../../../theme/Spacing";
import { AtiraIcon } from "../../../../../components/AtiraIcon";
import { faEllipsis } from "@fortawesome/free-solid-svg-icons/faEllipsis";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons/faWhatsapp";
import { Textarea } from "../../../../../components/Textarea";
import { AtiraSelect } from "../../../../../components/AtiraSelect";
import { useAppSelector } from "../../../../../redux/store";
import { entrySliceSelectors } from "../../../../../redux/entry/entry.selector";
import { EntityIcons } from "../../../../../utils/GlobalIcons";
import { CreateChatbotDto } from "../../../../../model/chatbot/dto/CreateChatbotDto";
import { UpdateChatbotDto } from "../../../../../model/chatbot/dto/UpdateChatbotDto";
import { formSliceSelectors } from "../../../../../redux/forms/form.selector";
import { InputTypes } from "../../../../../model/form/types/InputTypes.enum";

const Container = styled(Flex)`
  gap: ${Spacing.xl};
  width: 50%;
  flex-wrap: wrap;
  align-self: flex-start;
  flex-direction: column;
`;

export const SettingsAIChatBotStepExtraActions = () => {
  const { control, watch, setValue } = useFormContext<
    CreateChatbotDto | UpdateChatbotDto
  >();
  const { t } = useTranslation();

  const selectedFormId = watch("formId");
  const selectedForm = useAppSelector(formSliceSelectors.selectForms)?.find(
    (form) => form._id === selectedFormId,
  );

  const tableSettings = useAppSelector(
    entrySliceSelectors.selectUserEntryTableSettings,
  );

  const useableColumns = tableSettings?.columns
    .filter((column) => column.custom)
    .filter((column) => {
      if (!selectedForm) {
        return true;
      }

      const field = selectedForm?.inputs?.find(
        (field) => field.name === column.field,
      );

      if (
        [InputTypes.DATE, InputTypes.CHECKBOX, InputTypes.NUMBER].includes(
          field?.type as InputTypes,
        )
      ) {
        return false;
      }

      return true;
    });

  return (
    <Container>
      <Flex alignItems="center" gap="s">
        <AtiraIcon icon={faEllipsis} color="main" size="xl" />

        <Text fontSize="l" fontWeight={"bold"} color="main">
          {t("settings.aiChatbot.sections.extraActions.title")}
        </Text>
      </Flex>

      <Flex flexDirection="column" gap="l">
        <Flex alignItems="center" gap="s">
          <AtiraIcon icon={faWhatsapp} color="main" size="xl" />

          <Text fontSize="m" fontWeight={"bold"} color="main">
            {t("settings.aiChatbot.extraActions.whatsApp")}
          </Text>
        </Flex>
        <Controller
          name="extraActions.whatsApp.phoneNumber"
          control={control}
          render={({ fieldState: { error }, field: { onChange, value } }) => (
            <Input
              type="tel"
              value={value}
              onChange={onChange}
              title={t("settings.aiChatbot.extraActions.whatsApp.title")}
              placeholder={t(
                "settings.aiChatbot.extraActions.whatsApp.placeholder",
              )}
              info={t("settings.aiChatbot.extraActions.whatsApp.info")}
              valid={!error?.message}
              errorMessage={error?.message}
              maxLength={Lengths.LONG}
              dataTestId={TestIDs.chatbot.drawer.createEdit.fields.whatsApp}
            />
          )}
        />
        <Controller
          name="extraActions.whatsApp.starterMessage"
          control={control}
          render={({ fieldState: { error }, field: { onChange, value } }) => (
            <Textarea
              value={value}
              onChange={onChange}
              title={t(
                "settings.aiChatbot.extraActions.whatsApp.starterMessage.title",
              )}
              placeholder={t(
                "settings.aiChatbot.extraActions.whatsApp.starterMessage.placeholder",
              )}
              info={t(
                "settings.aiChatbot.extraActions.whatsApp.starterMessage.info",
              )}
              valid={!error?.message}
              errorMessage={error?.message}
              maxLength={Lengths.LONG}
              rows={4}
              dataTestId={
                TestIDs.chatbot.drawer.createEdit.fields.whatsAppStarterMessage
              }
            />
          )}
        />

        <Flex alignItems="center" gap="s">
          <AtiraIcon icon={EntityIcons.Entries} color="main" size="xl" />

          <Text fontSize="m" fontWeight={"bold"} color="main">
            {t("settings.aiChatbot.extraActions.leadSummary.title")}
          </Text>
        </Flex>
        <Controller
          name="extraActions.leadSummary.summaryField"
          control={control}
          render={({ fieldState: { error }, field: { onChange, value } }) => (
            <Flex flexDirection="column" gap="s">
              <AtiraSelect
                value={value}
                options={[
                  {
                    label: t("common.dropdown.select"),
                    value: "",
                  },
                  ...(useableColumns || [])?.map((column) => ({
                    label: column.label,
                    value: column.field,
                    "data-testid":
                      TestIDs.chatbot.drawer.createEdit.fields.leadSummarySummaryField.option(
                        column.field,
                      ),
                  })),
                ]}
                onChange={onChange}
                title={t(
                  "settings.aiChatbot.extraActions.leadSummary.summaryField.title",
                )}
                info={t(
                  "settings.aiChatbot.extraActions.leadSummary.summaryField.info",
                )}
                valid={!error?.message}
                errorMessage={error?.message}
                maxLength={Lengths.LONG}
                className={
                  TestIDs.chatbot.drawer.createEdit.fields
                    .leadSummarySummaryField.open
                }
              />
              <Info
                info={t(
                  "settings.aiChatbot.extraActions.leadSummary.summaryField.description",
                )}
              />
              <Info
                color="warning"
                info={t(
                  "settings.aiChatbot.extraActions.leadSummary.summaryField.description-2",
                )}
              />
            </Flex>
          )}
        />
      </Flex>
    </Container>
  );
};
